import { isToday, isYesterday, startOfDay, subDays } from 'shared/dateFns'

import { type IndividualConfig, type GroupedBySourceAndSeverityConfig } from './useNotificationPresentationConfig'
import { type DisplayNotification } from './utils'

import { type Notification, type UserNotificationResp, type NotificationSource } from 'shared/api/notifications'

export interface GroupingAccumulator {
  individuals: UserNotificationResp[]
  grouped: Record<string, Record<string, UserNotificationResp[]>>
}

export function getNotificationSourceKey(source: NotificationSource, metadata: object): DisplayNotification['source'] {
  return source.type === 'job_statuses' && 'source' in metadata
    ? ((metadata as { source: string }).source as DisplayNotification['source'])
    : source.type
}

export const getDateKey = (date: Date): string => {
  if (isToday(date)) {
    return 'Today'
  }
  if (isYesterday(date)) {
    return 'Yesterday'
  }
  const sevenDaysAgoCutoff = startOfDay(subDays(new Date(), 6))
  if (date >= sevenDaysAgoCutoff) {
    return 'Last 7 days'
  }
  return 'Older'
}

const DATE_KEY_SORT_ORDER: Record<string, number> = {
  Today: 1,
  Yesterday: 2,
  'Last 7 days': 3,
  Older: 4
}

export const sortDateKeys = (a: string, b: string): number => {
  const aOrder = DATE_KEY_SORT_ORDER[a] ?? 5
  const bOrder = DATE_KEY_SORT_ORDER[b] ?? 5
  return aOrder - bOrder
}

function addToGroup(grouped: GroupingAccumulator['grouped'], notification: UserNotificationResp): void {
  const { source, severity, metadata } = notification.notification
  const groupKey = getNotificationSourceKey(source, metadata)

  grouped[groupKey] ??= {}
  grouped[groupKey][severity] ??= []
  grouped[groupKey][severity].push(notification)
}

export const notificationReducer = (
  acc: Record<string, GroupingAccumulator>,
  notification: UserNotificationResp,
  notificationPresentationConfig: Record<string, any>
): Record<string, GroupingAccumulator> => {
  const sourceNotification = notification.notification
  const notificationDate = new Date(notification.createdAt)
  const dateKey = getDateKey(notificationDate)

  acc[dateKey] ??= { individuals: [], grouped: {} }

  const groupKey = getNotificationSourceKey(sourceNotification.source, sourceNotification.metadata)

  const config = notificationPresentationConfig[groupKey]?.[sourceNotification.severity]

  if (config === undefined) {
    return acc
  }

  if (config.presentationType === 'individual') {
    acc[dateKey].individuals.push(notification)
  } else {
    addToGroup(acc[dateKey].grouped, notification)
  }

  return acc
}

export const processIndividualNotifications = (
  individuals: UserNotificationResp[],
  notificationPresentationConfig: Record<string, any>
): DisplayNotification[] => {
  return individuals.map((notification) => {
    const sourceType = getNotificationSourceKey(notification.notification.source, notification.notification.metadata)

    const config = notificationPresentationConfig[sourceType]?.[notification.notification.severity] as IndividualConfig

    const title = config.title(notification.notification)
    const message = config.message(notification.notification)
    const sourceKey = getNotificationSourceKey(notification.notification.source, notification.notification.metadata)

    return {
      source: sourceKey,
      title,
      message,
      createdAt: new Date(notification.createdAt),
      id: notification.notification.id,
      clientId: notification.clientId,
      updatedAt: new Date(notification.updatedAt),
      updatedBy: new Date(notification.updatedBy),
      createdBy: notification.notification.createdBy,
      severity: notification.notification.severity,
      tags: notification.notification.tags,
      notificationIds: [notification.id],
      isRead: notification.isRead
    }
  })
}

export const processGroupedNotifications = (
  grouped: GroupingAccumulator['grouped'],
  notificationPresentationConfig: Record<string, any>
): DisplayNotification[] => {
  /**
   * Processes grouped notifications and returns an array of DisplayNotification objects.
   *
   * Each group is defined by a source and severity. For each group, this function:
   * - Finds the latest createdAt timestamp among the notifications in the group.
   * - Looks up the presentation config for the group.
   * - If the config's presentationType is 'group_by_source_and_severity', creates a DisplayNotification
   *   summarizing the group (with count, title, message, etc).
   *
   * @param grouped - The grouped notifications object, keyed by source and severity.
   * @returns An array of DisplayNotification objects representing grouped notifications.
   */
  const items: DisplayNotification[] = []

  Object.keys(grouped).forEach((sourceKey) => {
    Object.keys(grouped[sourceKey]).forEach((severityKey) => {
      const notificationsForGroup = grouped[sourceKey][severityKey as Notification['severity']]

      if (notificationsForGroup.length > 0) {
        const latestTimestamp = Math.max(...notificationsForGroup.map((n) => new Date(n.createdAt).getTime()))
        const latestCreatedAt = new Date(latestTimestamp)

        const configKey = getNotificationSourceKey(
          notificationsForGroup[0]?.notification.source,
          notificationsForGroup[0]?.notification.metadata
        )

        const config = notificationPresentationConfig[configKey as Notification['source']['type']]?.[
          severityKey as Notification['severity']
        ] as GroupedBySourceAndSeverityConfig

        if (config?.presentationType === 'group_by_source_and_severity') {
          const isRead = notificationsForGroup.every((n) => n.isRead)

          items.push({
            source: sourceKey as Notification['source']['type'],
            severity: severityKey as Notification['severity'],
            title: config.title(
              notificationsForGroup.length,
              configKey as NotificationSource['type'],
              severityKey as Notification['severity']
            ),
            message: config.message(notificationsForGroup.length),
            count: notificationsForGroup.length,
            createdAt: latestCreatedAt,
            notificationIds: notificationsForGroup.map((n) => n.id),
            isRead
          })
        }
      }
    })
  })

  return items
}
